import { useEffect, useId, useRef } from 'react'

/**
 * The author's reply to the peer review that returned the task, sent with the resubmission.
 * The submit button lives in the next-step rail; when it is pressed without a reply, `missing`
 * (a count of those attempts, 0 for none) marks this field and brings it into view each time.
 */
export function PeerReply({ value, onChange, missing, disabled = false }: {
  value: string; onChange: (value: string) => void; missing: number | boolean; disabled?: boolean
}) {
  const id = useId()
  const marked = !!missing
  const field = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (!missing) return
    field.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    field.current?.focus({ preventScroll: true })
  }, [missing])
  return (
    <section className="box peer-reply" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`}>Your reply to the peer review</h3>
      <p className="hint" id={`${id}-hint`}>
        Say which requested changes you made and which you chose not to make, and why. The reviewer reads this next to their feedback.
      </p>
      <textarea ref={field} value={value} maxLength={8000} disabled={disabled} aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-hint${marked ? ` ${id}-error` : ''}`} aria-invalid={marked || undefined}
        className={marked ? 'is-invalid' : undefined} onChange={(event) => onChange(event.target.value)} />
      {marked && <p className="field-error" id={`${id}-error`}>Write your reply before submitting for peer review.</p>}
    </section>
  )
}
